import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Loader2 } from 'lucide-react'
import type { ReactNode } from 'react'
import { toast } from 'sonner'
import { queryKeys } from '@/shared/constants/query-keys'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger
} from '@/shared/ui/alert-dialog'
import { handleMutationError } from '@/shared/utils/handle-api-error'
import { useReservationCommands } from '../hooks/use-reservation-commands'
import type { Reservation } from '../models/reservation-source'

interface AuthorizeReservationDialogProps {
  reservation: Reservation
  propertyName: string
  reservedByName?: string
  children: ReactNode
}

export function AuthorizeReservationDialog({
  reservation,
  propertyName,
  reservedByName,
  children
}: AuthorizeReservationDialogProps) {
  const queryClient = useQueryClient()
  const commands = useReservationCommands()

  const mutation = useMutation({
    mutationFn: async () => await commands.authorize(reservation),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.availability.all })
      toast.success('Solicitud de apartado aceptada')
    },
    onError: (error) => {
      handleMutationError(error)
      queryClient.invalidateQueries({ queryKey: queryKeys.availability.all })
    }
  })

  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>{children}</AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Confirmar Autorización</AlertDialogTitle>
          <AlertDialogDescription>
            {reservedByName ? (
              <>
                ¿Estás seguro de aceptar la solicitud de apartado de{' '}
                <strong>{reservedByName}</strong> para <strong>{propertyName}</strong>?
              </>
            ) : (
              <>
                ¿Estás seguro de aceptar la solicitud de apartado para{' '}
                <strong>{propertyName}</strong>?
              </>
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={mutation.isPending}>Cancelar</AlertDialogCancel>
          <AlertDialogAction
            onClick={(e) => {
              e.preventDefault()
              mutation.mutate()
            }}
            disabled={mutation.isPending}
          >
            {mutation.isPending && <Loader2 className="size-4 animate-spin" />}
            Aceptar Solicitud
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
